/**
 * AiPriorityBriefing — the dark "command center" briefing card that
 * replaces the old Hero stat tiles + Needs Attention rail on the Jobs
 * page.
 *
 * Given the current visible-scope event list and the AIOpsEngine
 * summary, this shows 3-6 plain-English recommendations. Each is a
 * button — clicking narrows the parent list to exactly the shifts the
 * recommendation is talking about (and updates the AI category chip
 * strip in lock-step).
 *
 * Design goals per user spec (2026-08-05):
 *   - "AI should guide operations" (plain-English priorities)
 *   - "Reduce visual complexity"   (one panel, not three)
 *   - "Every count must match results"  (parent passes scoped totals)
 *   - Dark enterprise UI            (matches Command Center on marketing site)
 */
import React from 'react';
import {
  Sparkles, AlertOctagon, ClipboardList, UserCheck,
  MessageCircleWarning, ShieldAlert, CalendarClock, ArrowRight,
  CheckCircle2,
} from 'lucide-react';

const ICON_FOR_CATEGORY = {
  critical:              AlertOctagon,
  at_risk:               ShieldAlert,
  needs_applicants:      UserCheck,
  reviewing_applicants:  ClipboardList,
  awaiting_confirmation: MessageCircleWarning,
  backup_needed:         CalendarClock,
  briefing_incomplete:   ClipboardList,
};

/**
 * Build the list of prioritized recommendations based on which
 * categories have shifts in the current scope. Highest-urgency
 * categories come first.
 *
 * Every entry: { category, headline, count, icon, tint }
 */
function buildBriefing(countsByCategory) {
  const c = countsByCategory || {};
  const items = [];

  if (c.critical > 0)
    items.push({
      category: 'critical',
      headline: `${c.critical} event${c.critical === 1 ? '' : 's'} in critical status`,
      subtext:  'Start within 48 hours & still unfilled — act now',
      count:    c.critical,
      tint:     'rose',
    });

  if (c.at_risk > 0)
    items.push({
      category: 'at_risk',
      headline: `${c.at_risk} event${c.at_risk === 1 ? ' is' : 's are'} at risk`,
      subtext:  'Within 7 days & still short-staffed',
      count:    c.at_risk,
      tint:     'orange',
    });

  if (c.needs_applicants > 0)
    items.push({
      category: 'needs_applicants',
      headline: `${c.needs_applicants} shift${c.needs_applicants === 1 ? '' : 's'} need${c.needs_applicants === 1 ? 's' : ''} applicants`,
      subtext:  'Zero applicants received — expand invites',
      count:    c.needs_applicants,
      tint:     'amber',
    });

  if (c.reviewing_applicants > 0)
    items.push({
      category: 'reviewing_applicants',
      headline: `${c.reviewing_applicants} shift${c.reviewing_applicants === 1 ? '' : 's'} awaiting your review`,
      subtext:  'Applicants ready — approve to fill the shift',
      count:    c.reviewing_applicants,
      tint:     'blue',
    });

  if (c.awaiting_confirmation > 0)
    items.push({
      category: 'awaiting_confirmation',
      headline: `${c.awaiting_confirmation} shift${c.awaiting_confirmation === 1 ? '' : 's'} awaiting confirmation`,
      subtext:  'Day-of check-in still outstanding',
      count:    c.awaiting_confirmation,
      tint:     'violet',
    });

  if (c.backup_needed > 0)
    items.push({
      category: 'backup_needed',
      headline: `${c.backup_needed} shift${c.backup_needed === 1 ? '' : 's'} still need backup coverage`,
      subtext:  'Primaries are set, but backup slots are open',
      count:    c.backup_needed,
      tint:     'violet',
    });

  if (c.briefing_incomplete > 0)
    items.push({
      category: 'briefing_incomplete',
      headline: `${c.briefing_incomplete} briefing${c.briefing_incomplete === 1 ? '' : 's'} incomplete`,
      subtext:  'Event details missing before start',
      count:    c.briefing_incomplete,
      tint:     'violet',
    });

  return items.slice(0, 6);
}

const TINTS = {
  rose:   { chip: 'text-rose-300 bg-rose-500/10 border-rose-500/40',       ring: 'hover:border-rose-400/60',   text: 'text-rose-100' },
  orange: { chip: 'text-orange-300 bg-orange-500/10 border-orange-500/40', ring: 'hover:border-orange-400/60', text: 'text-orange-100' },
  amber:  { chip: 'text-amber-300 bg-amber-500/10 border-amber-500/40',    ring: 'hover:border-amber-400/60',  text: 'text-amber-100' },
  blue:   { chip: 'text-sky-300 bg-sky-500/10 border-sky-500/40',          ring: 'hover:border-sky-400/60',    text: 'text-sky-100' },
  violet: { chip: 'text-violet-300 bg-violet-500/10 border-violet-500/40', ring: 'hover:border-violet-400/60', text: 'text-violet-100' },
};

export default function AiPriorityBriefing({
  countsByCategory,
  totals,
  onSelectCategory,
  onOpenBriefingCommand,
}) {
  const items = buildBriefing(countsByCategory);
  const totalShifts = totals?.shifts ?? 0;
  const attention = totals?.attention ?? 0;
  const hasBriefingIssue = (countsByCategory?.briefing_incomplete || 0) > 0;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-700/60 shadow-xl"
      data-testid="ai-priority-briefing"
    >
      {/* Subtle grid backdrop for the "command center" feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(1px 1px at 20% 20%, rgba(148,163,184,0.14) 1px, transparent 1px), radial-gradient(1px 1px at 80% 60%, rgba(148,163,184,0.12) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative px-4 md:px-6 py-4 md:py-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/30 to-sky-500/30 border border-violet-500/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-violet-200" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-300/90">AI Priority Briefing</div>
              <div className="text-sm text-slate-300">
                <span className="text-slate-100 font-semibold">{attention}</span> of {totalShifts} shifts need your attention
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-2">
            {hasBriefingIssue && onOpenBriefingCommand && (
              <button
                type="button"
                onClick={onOpenBriefingCommand}
                className="normal-case tracking-normal text-xs text-violet-300 hover:text-violet-100 underline decoration-dotted"
                data-testid="briefing-command-link"
              >
                Open Briefing Command →
              </button>
            )}
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </div>
        </div>

        {/* Recommendations */}
        {items.length === 0 ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-300">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            No priorities right now — every shift is on track.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {items.map((item) => {
              const Icon = ICON_FOR_CATEGORY[item.category] || Sparkles;
              const t = TINTS[item.tint] || TINTS.violet;
              return (
                <button
                  key={item.category}
                  type="button"
                  onClick={() => onSelectCategory?.(item.category)}
                  className={`group text-left rounded-xl border border-slate-700/60 bg-slate-900/40 backdrop-blur-sm px-3.5 py-3 transition ${t.ring} hover:bg-slate-900/70`}
                  data-testid={`briefing-rec-${item.category}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center ${t.chip}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-semibold ${t.text}`}>{item.headline}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{item.subtext}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-slate-200 group-hover:translate-x-0.5 transition mt-2" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
